import React, { useRef, useState, useEffect } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import RotatingText from "./blocks/RotatingText/RotatingText";
import ScrollReveal from "./blocks/ScrollReveal/ScrollReveal";
import TextPressure from "./blocks/TextPressure/TextPressure";
//import GooeyNav from "./blocks/GooeyNav/GooeyNav";
import ClickSpark from "./blocks/ClickSpark/ClickSpark";
import Dock from "./blocks/Dock/Dock";
import VariableProximity from "./blocks/VariableProximity/VariableProximity";
import ProjectsSection from "./components/ProjectsSection";
import MysticaReplica from "./pages/MysticaReplica";
import Amir from "./pages/Amir";
import Casserole from "./pages/Casserole";
import CamDown from "./pages/CamDown";
import Scrollden from "./pages/Scrollden";
import Gismonda from "./pages/Gismonda";
import Others from "./pages/Others";
import Dream from "./pages/Dream";
import DesignDocs from "./pages/DesignDocs";
import WebDev from "./pages/WebDev";
import LamentOfSwan from "./pages/LamentOfSwan";
import Sage from "./pages/Sage";
import Cautions from "./pages/Cautions";
import SeeMuseums from "./pages/SeeMuseums";
import AboutMe from "./pages/AboutMe";

import { detectBrowserLanguage, getTranslations, Language } from "./utils/localization";
import "./App.css";

function App() {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const [language, setLanguage] = useState<Language>('en');
  const [translations, setTranslations] = useState(getTranslations('en'));
  const [showEasterEgg, setShowEasterEgg] = useState(false);
  const easterEggTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Detect browser language on component mount
  useEffect(() => {
    const detectedLanguage = detectBrowserLanguage();
    setLanguage(detectedLanguage);
    setTranslations(getTranslations(detectedLanguage));
  }, []);

  // Toggle language function
  const toggleLanguage = (lang: Language) => {
    setLanguage(lang);
    setTranslations(getTranslations(lang));
    
    // Update the document language attribute
    document.documentElement.lang = lang === 'zh' ? 'zh-CN' : 'en';
    
    // Dispatch a custom event to notify other components of language change
    window.dispatchEvent(new Event('languagechange'));
  };

  const handleCopyrightMouseEnter = () => {
    easterEggTimeoutRef.current = setTimeout(() => {
      setShowEasterEgg(true);
    }, 500);
  };

  const handleCopyrightMouseLeave = () => {
    if (easterEggTimeoutRef.current) {
      clearTimeout(easterEggTimeoutRef.current);
      easterEggTimeoutRef.current = null;
    }
    setShowEasterEgg(false);
  };

  // const items = [
  //   { label: "Home", href: "#" },
  //   { label: "About", href: "#about" },
  //   { label: "Projects", href: "#projects" },
  //   { label: "Contact", href: "#contact" },
  // ];

  const dockItems = [
    {
      icon: <img src="/images/dock/resume.png" alt="Resume" />,
      label: "Resume",
      onClick: () => window.open(language === 'zh' ? "/MG_resume_zh_noms.pdf" : "/MG_resume_en_ms.pdf", "_blank"),
      className: "dock-resume"
    },
    {
      icon: <img src="/images/dock/insta.png" alt="Instagram" />,
      label: "Instagram",
      onClick: () => window.open("https://www.instagram.com/xaabehntx", "_blank"),
      className: "dock-instagram"
    },
    {
      icon: <img src="/images/dock/itch.png" alt="Itch.io" />,
      label: "Itch.io",
      onClick: () => window.open("https://bellinimoon.itch.io/", "_blank"),
      className: "dock-itch"
    },
    {
      icon: <img src="/images/dock/linkedln.png" alt="LinkedIn" />,
      label: "LinkedIn",
      onClick: () => window.open("https://linkedin.com/in/mengxian-gao", "_blank"),
      className: "dock-linkedin"
    },
    {
      icon: <img src="/images/dock/github.png" alt="GitHub" />,
      label: "GitHub",
      onClick: () => window.open("https://github.com/bellinimoon", "_blank"),
      className: "dock-github"
    }
  ];

  const isHomePage = location.pathname === '/';

  return (
    <div className="app-container" ref={scrollContainerRef}>
      <Routes>
        <Route path="/dream" element={<Dream />} />
        <Route path="/mystica-replica" element={<MysticaReplica />} />
        <Route path="/amir" element={<Amir />} />
        <Route path="/casserole" element={<Casserole />} />
        <Route path="/scrollden" element={<Scrollden />} />
        <Route path="/gismonda" element={<Gismonda />} />
        <Route path="/other" element={<Others />} />
        <Route path="/design-docs" element={<DesignDocs />} />
        <Route path="/webdev" element={<WebDev />} />
        <Route path="/cam-down" element={<CamDown />} />
        <Route path="/lament-of-swan" element={<LamentOfSwan />} />
        <Route path="/about-me" element={<AboutMe />} />
        <Route path="/sage" element={<Sage />} />
        <Route path="/cautions" element={<Cautions />} />
        <Route path="/seemuseums" element={<SeeMuseums />} />


        <Route path="/" element={
          <>
            <header className="header" ref={headerRef}>
              <div className="logo">
                <img src="/symbol_big.png" alt="Logo" className="logo-image" />
                <div className="logo-text">
                  <VariableProximity
                    containerRef={headerRef}
                    label="Mengxian"
                    fromFontVariationSettings="'wght' 600"
                    toFontVariationSettings="'wght' 900, 'ital' 1"
                    radius={100}
                    falloff="gaussian"
                    style={{ fontSize: '1.3rem', fontWeight: 600, lineHeight: '1' }}
                  />
                  <VariableProximity
                    containerRef={headerRef}
                    label="Gao"
                    fromFontVariationSettings="'wght' 600"
                    toFontVariationSettings="'wght' 900, 'ital' 1"
                    radius={100}
                    falloff="gaussian"
                    style={{ fontSize: '1.3rem', fontWeight: 600, lineHeight: '1' }}
                  />
                </div>
              </div>
              <nav className="nav">
                <ul>
                  <li><a href="/about-me">{translations.nav.about}</a></li>
                  <li><a href="#projects">{translations.nav.projects}</a></li>
                  <li><a href="#contact">{translations.nav.contact}</a></li>
                </ul>
              </nav>
              
              <div className="language-switcher">
                <button 
                  className={`lang-btn ${language === 'en' ? 'active' : ''}`}
                  onClick={() => toggleLanguage('en')}
                >
                  EN
                </button>
                <button 
                  className={`lang-btn ${language === 'zh' ? 'active' : ''}`}
                  onClick={() => toggleLanguage('zh')}
                >
                  中文
                </button>
              </div>
            </header>

            <main>
              <ClickSpark
                shape="heart"
                sparkColor='#BD2735'
                sparkSize={8}
                sparkRadius={40}
                sparkCount={16}
                duration={500}
              >
                <section className="hero">
                  <div style={{ position: 'relative', height: '100px', marginBottom: '2rem' }}>
                    <TextPressure
                      text="Mengxian Gao"
                      flex={true}
                      alpha={false}
                      stroke={false}
                      width={true}
                      weight={true}
                      italic={true}
                      textColor="#266867"
                      minFontSize={80}
                    />
                  </div>
                  <div className="title-container">
                    <span className="title-prefix">{translations.hero.prefix} </span>
                    <RotatingText 
                      texts={translations.hero.roles}
                      rotationInterval={2000}
                      staggerDuration={0.015}
                      staggerFrom={"last"}
                      initial={{ y: "100%" }}
                      animate={{ y: 0 }}
                      exit={{ y: "-120%" }}
                      transition={{ type: "spring", damping: 30, stiffness: 400 }}
                      mainClassName="title-rotating py-1 px-4 justify-center"
                      splitLevelClassName="overflow-hidden"
                    />
                  </div>
                </section>

                <section className="about" id="about">
                  <ScrollReveal 
                    baseOpacity={0}
                    enableBlur={true}
                    baseRotation={10}
                    blurStrength={15}
                  >
                    {translations.about.content}
                  </ScrollReveal>
                </section>
                
                <ProjectsSection />
              </ClickSpark>
            </main>

            <footer className="footer" id="contact">
              <div className="footer-left">
                <p><a href="mailto:mg6839@nyu.edu" onClick={(e) => { window.location.href = 'mailto:mg6839@nyu.edu'; }}>mg6839@nyu.edu</a></p>
                <p>682-553-4790</p>
              </div>
              <div className="footer-right">
                <p 
                  onMouseEnter={handleCopyrightMouseEnter}
                  onMouseLeave={handleCopyrightMouseLeave}
                >
                  {showEasterEgg ? "Made with love by YW" : translations.footer.copyright}
                </p>
              </div>
            </footer>
          </>
        } />
      </Routes>
      
      {isHomePage && (
        <Dock
          items={dockItems}
          magnification={90}
          distance={180}
          baseItemSize={60}
          panelHeight={80}
        />
      )}
    </div>
  );
}

export default App; 